export interface GraphCamera {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  scale: number;
  targetX: number;
  targetY: number;
  targetScale: number;
}

export const CAMERA_DECAY = 0.88;
export const CAMERA_STOP_THRESHOLD = 0.25;
export const CAMERA_TARGET_THRESHOLD = 0.5;
export const CAMERA_SCALE_THRESHOLD = 0.01;
export const CAMERA_SPRING = 0.18;
export const CAMERA_ZOOM_SPRING = 0.16;
export const CAMERA_MIN_SCALE = 0.65;
export const CAMERA_MAX_SCALE = 2.4;
export const CAMERA_WHEEL_SENSITIVITY = 0.0012;
export const DRAG_DISTANCE_THRESHOLD = 6;

export interface FocusPoint {
  x: number;
  y: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface CameraBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function createGraphCamera(): GraphCamera {
  return { x: 0, y: 0, velocityX: 0, velocityY: 0, scale: 1, targetX: 0, targetY: 0, targetScale: 1 };
}

export function panCamera(camera: GraphCamera, deltaX: number, deltaY: number): GraphCamera {
  return {
    ...camera,
    x: camera.x + deltaX,
    y: camera.y + deltaY,
    targetX: camera.targetX + deltaX,
    targetY: camera.targetY + deltaY,
  };
}

export function setCameraVelocity(camera: GraphCamera, velocityX: number, velocityY: number): GraphCamera {
  return {
    ...camera,
    velocityX,
    velocityY,
  };
}

export function setCameraTarget(camera: GraphCamera, targetX: number, targetY: number, targetScale = camera.targetScale): GraphCamera {
  return {
    ...camera,
    targetX,
    targetY,
    targetScale,
  };
}

export function focusCameraOnNode(
  camera: GraphCamera,
  point: FocusPoint,
  viewport: ViewportSize,
  targetScale = 1.55
): GraphCamera {
  return setCameraTarget(
    camera,
    viewport.width / 2 - point.x * targetScale,
    viewport.height / 2 - point.y * targetScale,
    targetScale
  );
}

export function fitCameraToBounds(
  camera: GraphCamera,
  bounds: CameraBounds,
  viewport: ViewportSize,
  padding = 140
): GraphCamera {
  const width = Math.max(240, bounds.maxX - bounds.minX);
  const height = Math.max(240, bounds.maxY - bounds.minY);
  const availableWidth = Math.max(320, viewport.width - padding * 2);
  const availableHeight = Math.max(240, viewport.height - padding * 2);
  const targetScale = clamp(Math.min(availableWidth / width, availableHeight / height), 0.72, 1);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return setCameraTarget(
    camera,
    viewport.width / 2 - centerX * targetScale,
    viewport.height / 2 - centerY * targetScale,
    targetScale
  );
}

export function zoomCameraAtCanvasPoint(
  camera: GraphCamera,
  canvasPoint: FocusPoint,
  deltaY: number,
  minScale = CAMERA_MIN_SCALE,
  maxScale = CAMERA_MAX_SCALE
): GraphCamera {
  const worldX = (canvasPoint.x - camera.x) / camera.scale;
  const worldY = (canvasPoint.y - camera.y) / camera.scale;
  const zoomFactor = Math.exp(-deltaY * CAMERA_WHEEL_SENSITIVITY);
  const targetScale = clamp(camera.targetScale * zoomFactor, minScale, maxScale);

  if (targetScale === camera.targetScale) {
    return camera;
  }

  return setCameraTarget(
    camera,
    canvasPoint.x - worldX * targetScale,
    canvasPoint.y - worldY * targetScale,
    targetScale
  );
}

export function advanceCameraInertia(
  camera: GraphCamera,
  decay = CAMERA_DECAY,
  stopThreshold = CAMERA_STOP_THRESHOLD
): GraphCamera {
  const next = {
    ...camera,
    x: camera.x + camera.velocityX,
    y: camera.y + camera.velocityY,
    velocityX: camera.velocityX * decay,
    velocityY: camera.velocityY * decay,
    scale: camera.scale + (camera.targetScale - camera.scale) * CAMERA_ZOOM_SPRING,
  };

  if (Math.abs(next.velocityX) < stopThreshold) {
    next.velocityX = 0;
  }

  if (Math.abs(next.velocityY) < stopThreshold) {
    next.velocityY = 0;
  }

  next.x += (next.targetX - next.x) * CAMERA_SPRING;
  next.y += (next.targetY - next.y) * CAMERA_SPRING;

  if (Math.abs(next.targetX - next.x) < CAMERA_TARGET_THRESHOLD) {
    next.x = next.targetX;
  }

  if (Math.abs(next.targetY - next.y) < CAMERA_TARGET_THRESHOLD) {
    next.y = next.targetY;
  }

  if (Math.abs(next.targetScale - next.scale) < CAMERA_SCALE_THRESHOLD) {
    next.scale = next.targetScale;
  }

  return next;
}

export function isCameraMoving(camera: GraphCamera): boolean {
  return camera.velocityX !== 0
    || camera.velocityY !== 0
    || Math.abs(camera.targetX - camera.x) >= CAMERA_TARGET_THRESHOLD
    || Math.abs(camera.targetY - camera.y) >= CAMERA_TARGET_THRESHOLD
    || Math.abs(camera.targetScale - camera.scale) >= CAMERA_SCALE_THRESHOLD;
}

export function isDragGesture(deltaX: number, deltaY: number, threshold = DRAG_DISTANCE_THRESHOLD): boolean {
  return Math.hypot(deltaX, deltaY) >= threshold;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}